// Component to render DAU Chart
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const DauChart = ({ data, interval, graphName, dataTitle }) => {
  // Function to format DAU data for chart based on interval type
  const formatDauDataForChart = (data, intervalLabel) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    const firstItem = data[0];

    // Check if it's year data (number as first element)
    if (typeof firstItem[0] === "number" && firstItem[0] > 1000) {
      return data.map((item) => ({
        name: item[0].toString(),
        users: item[1],
        label: `${item[0]}`,
      }));
    }

    // Check if it's month data (has 3 elements: month_num, month_name, count)
    if (firstItem.length === 3) {
      return data.map((item) => ({
        name: item[1].trim(),
        users: item[2],
        label: item[1].trim(),
      }));
    }

    // Check if it's date data (string in YYYY-MM-DD format or timestamp)
    if (typeof firstItem[0] === "string") {
      return data.map((item) => {
        const dateStr = item[0];
        let displayName;

        if (dateStr.includes(" ")) {
          const date = new Date(dateStr);
          if (intervalLabel.includes("Minute")) {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            });
          } else {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              hour12: true,
            });
          }
        } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const date = new Date(dateStr);
          displayName = date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        } else {
          displayName = dateStr;
        }

        return {
          name: displayName,
          users: item[1],
          label: displayName,
        };
      });
    }

    return data.map((item, index) => ({
      name: `Point ${index + 1}`,
      users: Array.isArray(item) ? item[1] : item,
      label: `Point ${index + 1}`,
    }));
  };

  const chartData = formatDauDataForChart(data, interval);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-placeholder">
        📊 No data available
        <small>Try selecting a different date range</small>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="name"
          stroke="#718096"
          style={{ fontSize: "12px" }}
          angle={chartData.length > 10 ? -45 : 0}
          textAnchor={chartData.length > 10 ? "end" : "middle"}
          height={chartData.length > 10 ? 60 : 30}
        />
        <YAxis
          stroke="#718096"
          style={{ fontSize: "12px" }}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            fontSize: "12px",
          }}
          formatter={(value) => [value.toLocaleString(), dataTitle]}
        />
        <Legend
          wrapperStyle={{ fontSize: "12px" }}
          formatter={() => graphName}
        />
        <Line
          type="monotone"
          dataKey="users"
          stroke="#667eea"
          strokeWidth={2}
          dot={{ fill: "#667eea", r: 4 }}
          activeDot={{ r: 6 }}
          name={graphName}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default DauChart;
